/**
 * All of our CC URL routes. You may navigate to any route by providing the route
 * and an argument specifying all it's route params, e.g. { taskId: 1, contactId: 3}.
 *
 * Some routes are special values that map to one of the other routes depending on current location context.
 */
export enum Route {
  LOBBY = 'leasing/lobby',
  INBOX = 'leasing/inbox',
  INBOX_THREAD = 'leasing/inbox/thread/:threadId',
  INBOX_THREAD_CONTACT = 'leasing/inbox/thread/:taskId/:contactId',
  TASKS = 'leasing/tasks',
  TASK = 'leasing/tasks/task/:taskId',
  TASK_THREAD = 'leasing/tasks/thread/:threadId',
  TASK_THREAD_CONTACT = 'leasing/tasks/thread/:taskId/:contactId',
  NEW_TASK = 'leasing/tasks/new',
  ADMIN_ENGINEERING = 'leasing/admin',
  ADMIN_ENGINEER = 'leasing/admin/engineering/:userId',
  ADMIN_OPERATIONS = 'leasing/admin/operations',
  ADMIN_OPERATIONS_USER = 'leasing/admin/operations/:userId',
  ADMIN_SUPPORT = 'leasing/admin/support',
  ADMIN_SUPPORT_USER = 'leasing/admin/support/:userId',
  ADMIN_OTHERS = 'leasing/admin/other',
  ADMIN_OTHER = 'leasing/admin/other/:userId',
  ADMIN_FIRMS = 'leasing/admin/firms',
  ADMIN_FIRM = 'leasing/admin/firms/:firmId',
  ADMIN_NEW_FIRM = 'leasing/admin/firms/new',
  ADMIN_SETTINGS = 'leasing/admin/settings',
  BULK_UPDATE = 'leasing/tasks/bulk_update/firm/:firmId',
  STATUS = 'leasing/status',
  FIREHOSE = 'leasing/firehose',
  COMPONENTS = 'leasing/components',
  RELEASE_NOTES = 'leasing/release-notes',

  /**
   * Not a literal URL route, this maps to one of [TASK_THREAD, TASK_THREAD_CONTACT, INBOX_THREAD, INBOX_THREAD_CONTACT].
   */
  THREAD = 'THREAD',
}

/**
 * Returns the URL path of a thread. The URL will depend on the current context (e.g. 'inbox', 'tasks').
 */
export function getThreadPath(threadId: number) {
  return getPath(Route.THREAD, { threadId })
}

/**
 * Returns the URL path of a thread. The URL will depend on the current context.
 */
export function getThreadPathFor(arg: { contactId: number; taskId: number }) {
  return getPath(Route.THREAD, arg)
}

/**
 * Returns the URL path of a task.
 */
export function getTaskPath(taskId: number) {
  return getPath(Route.TASK, { taskId })
}

/**
 * Returns the URL path of a firm.
 */
export function getFirmPath(firmId: number) {
  return getPath(Route.ADMIN_FIRM, { firmId })
}

/**
 * Example: getPath(ROUTES.TASK) returns "/leasing/tasks" while getPath(ROUTES.TASK, {taskId: 5}) returns "leasing/tasks/task/5".
 *
 * CAVEAT: currently this reads from window.location, the appropriate way to get location is through @reach/router.
 */
export function getPath(route: Route, arg?: Partial<ReturnType<typeof routeParams>>) {
  let routes = [route] as Route[]
  if (route === Route.THREAD) {
    routes = globalThis.location.pathname.startsWith('/leasing/tasks')
      ? [Route.TASK_THREAD, Route.TASK_THREAD_CONTACT]
      : [Route.INBOX_THREAD, Route.INBOX_THREAD_CONTACT]
  }

  for (const r of routes) {
    const params = r.split('/').filter(t => t.startsWith(':'))
    const keys = arg ? Object.keys(arg) : []
    const paramMatches = params.map(p => keys.includes(p.replace(':', ''))).filter(m => m)
    if (keys.length !== params.length || paramMatches.length < params.length) {
      continue // every parameter must be replaced
    }

    // matching case: arg specifies all params in the URL
    let path = r.toString()
    for (const k of keys) {
      path = path.replace(':' + k, '' + (arg as any)[k])
    }
    return '/' + path
  }

  throw new Error('no matching route')
}

/**
 * Represents parameters parsed from URL routes, e.g. /leasing/tasks/task/123 parses taskId=123.
 */
export interface AppRouteParams {
  taskId?: string
  threadId?: string
  contactId?: string
  firmId?: string
  userId?: string
}

/**
 * Parses string route params into numbers. Values are 0 where undefined. Useful for converting URL parameters into GraphQL query variables.
 */
export function routeParams(params: AppRouteParams) {
  return {
    taskId: Number(params.taskId || 0),
    threadId: Number(params.threadId || 0),
    contactId: Number(params.contactId || 0),
    firmId: Number(params.firmId || 0),
    userId: Number(params.userId || 0),
  }
}

type HashModel = 'message' | 'showing' | 'next-showing'

/**
 * Example: readHashId('showing', '#showing/1') returns 1
 *
 * Example: readHashId('showing', '#message/1') returns undefined
 */
export function readHashId(model: HashModel, hash: string): number | undefined {
  const prefix = '#' + model + '/'
  if (hash.startsWith(prefix)) {
    try {
      return Number.parseInt(hash.substring(prefix.length), 10)
    } catch (e) {
      /* ignore */
    }
  }
  return undefined
}

/**
 * Example: getHashForId('showing', 1) returns "#showing/1"
 */
export function getHash(model: HashModel, id: number): string {
  return '#' + model + '/' + id
}
